import { createContext, useCallback, useContext, useState, type ReactNode } from "react";
import clsx from "clsx";
import { ToneMarker } from "../status/StatusBadge";
import type { Tone } from "../status/statusMap";

export type ToastTone = "info" | "good" | "ok" | "bad" | "warn" | "neutral";
export interface ToastAction { label: string; onClick: () => void }
interface ToastItem { id: number; message: string; tone: Tone; action?: ToastAction }
export interface ToastOptions { tone?: ToastTone; action?: ToastAction; durationMs?: number }

const Ctx = createContext<{ push: (message: string, toneOrOptions?: ToastTone | ToastOptions) => void }>({ push: () => undefined });

/**
 * Toasts confirm what just happened ("Request sent to Kirsten") and may carry one action (Undo, View).
 * They never carry an error that needs action – use Banner or ErrorPanel for that.
 * `push(message, "good")` (legacy) and `push(message, { tone, action })` both work.
 */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const push = useCallback((message: string, toneOrOptions: ToastTone | ToastOptions = "info") => {
    const o: ToastOptions = typeof toneOrOptions === "string" ? { tone: toneOrOptions } : toneOrOptions;
    const tone: Tone = o.tone === "good" ? "ok" : (o.tone ?? "info");
    const id = Date.now() + Math.random();
    setToasts((t) => [...t, { id, message, tone, action: o.action }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), o.durationMs ?? (o.action ? 8000 : 5000));
  }, []);
  return (
    <Ctx.Provider value={{ push }}>
      {children}
      <div className="pointer-events-none fixed bottom-4 right-4 z-[60] flex max-w-[calc(100vw-2rem)] flex-col gap-2" aria-live="polite">
        {toasts.map((t) => (
          <div key={t.id} role="status" className="pointer-events-auto flex items-center gap-3 rounded-card bg-fg px-3.5 py-3 text-bg shadow-e3">
            <span className={clsx("inline-flex", { ok: "text-[#62c98f]", info: "text-[#86b6f0]", warn: "text-[#f2bb5a]", bad: "text-[#ff8f85]", neutral: "text-bg" }[t.tone])}><ToneMarker tone={t.tone} /></span>
            <span className="flex-1">{t.message}</span>
            {t.action && (
              <button type="button" className="rounded-tag px-1 text-[13px] font-semibold text-bg underline-offset-2 hover:underline" style={{ "--ring-gap": "var(--fg)" } as React.CSSProperties}
                onClick={() => { t.action?.onClick(); setToasts((x) => x.filter((y) => y.id !== t.id)); }}>{t.action.label}</button>
            )}
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export const useToast = () => useContext(Ctx);
